import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Node definitions: id, label, cx, cy
const NODE_DEFS = [
  { id: 'external',  label: 'External Internet', cx: 80,  cy: 250 },
  { id: 'corporate', label: 'Corporate IT',       cx: 250, cy: 250 },
  { id: 'dmz',       label: 'DMZ',                cx: 450, cy: 250 },
  { id: 'ot',        label: 'OT Control Zone',    cx: 650, cy: 250 },
  { id: 'field',     label: 'Field Devices',      cx: 820, cy: 250 },
];

// Adjacent connections (by index pairs)
const CONNECTIONS = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 4],
];

// Default colour state per node id
const DEFAULT_NODE_STATES = {
  external:  'red',
  corporate: 'amber',
  dmz:       'amber',
  ot:        'green',
  field:     'green',
};

const COLOR_MAP = {
  green: '#22c55e',
  amber: '#f59e0b',
  red:   '#ef4444',
};

/**
 * Compute SVG path string and total length from an array of {cx, cy} waypoints.
 */
function computePath(waypoints) {
  if (!waypoints || waypoints.length < 2) return { d: '', length: 0, times: [0, 1] };

  const d = waypoints
    .map((w, i) => `${i === 0 ? 'M' : 'L'}${w.cx},${w.cy}`)
    .join(' ');

  // Compute segment lengths
  const segLengths = waypoints.slice(1).map((w, i) => {
    const prev = waypoints[i];
    return Math.sqrt((w.cx - prev.cx) ** 2 + (w.cy - prev.cy) ** 2);
  });
  const totalLength = segLengths.reduce((s, l) => s + l, 0);

  // Normalised time fraction per waypoint [0 … 1]
  const times = [0];
  let accumulated = 0;
  for (const len of segLengths) {
    accumulated += len;
    times.push(totalLength > 0 ? accumulated / totalLength : 1);
  }

  return { d, length: Math.ceil(totalLength) + 10, times };
}

/**
 * NetworkTopology
 *
 * Props:
 *   nodeStates          — optional object keyed by node id ('external' | 'corporate' | 'dmz' | 'ot' | 'field')
 *                         with values 'green' | 'amber' | 'red'. Overrides defaults for named keys only.
 *   intrusionPath       — optional array of { cx, cy } waypoints defining the attack route.
 *                         When null/undefined the attack animation is not rendered (no regression).
 *                         When provided the path draws in over 1.5s, then fades out and calls onIntrusionComplete.
 *   onIntrusionComplete — optional callback fired when the intrusion animation finishes and fades out.
 */
function NetworkTopology({
  nodeStates: nodeStatesProp = {},
  intrusionPath = null,
  onIntrusionComplete,
  labelFontSize = 11,
}) {
  // Merge prop overrides onto defaults
  const resolvedStates = { ...DEFAULT_NODE_STATES, ...nodeStatesProp };

  // Visibility state for intrusion animation (drives fade-out)
  const [intrusionVisible, setIntrusionVisible] = useState(true);

  // Reset on each new intrusionPath
  useEffect(() => {
    if (!intrusionPath) return;
    setIntrusionVisible(true);
  }, [intrusionPath]);

  // Fire completion callback after draw-in (1.5s) + delay (0.5s) + buffer (0.3s)
  useEffect(() => {
    if (!intrusionPath) return;
    const TRAVEL_MS = (1.5 + 0.5) * 1000 + 300;
    const t = setTimeout(() => {
      setIntrusionVisible(false);
      onIntrusionComplete?.();
    }, TRAVEL_MS);
    return () => clearTimeout(t);
  }, [intrusionPath, onIntrusionComplete]);

  // Derive path geometry from waypoints
  const { d: pathD, length: pathLength, times } = intrusionPath
    ? computePath(intrusionPath)
    : { d: '', length: 0, times: [] };

  const attackCX = intrusionPath ? intrusionPath.map((w) => w.cx) : [];
  const attackCY = intrusionPath ? intrusionPath.map((w) => w.cy) : [];

  return (
    <svg
      viewBox="0 170 900 160"
      width="100%"
      role="img"
      aria-label="OT Network Topology Diagram"
      style={{ display: 'block' }}
    >
      {intrusionPath && pathLength > 0 && (
        <defs>
          <style>{`
            @keyframes svgDrawIn_${pathLength} {
              from { stroke-dashoffset: ${pathLength}; }
              to   { stroke-dashoffset: 0; }
            }
          `}</style>
        </defs>
      )}

      {/* ── Connections (drawn behind nodes) ── */}
      {CONNECTIONS.map(([fromIdx, toIdx]) => {
        const from = NODE_DEFS[fromIdx];
        const to   = NODE_DEFS[toIdx];
        return (
          <line
            key={`conn-${fromIdx}-${toIdx}`}
            x1={from.cx}
            y1={from.cy}
            x2={to.cx}
            y2={to.cy}
            stroke="#6b7280"
            strokeWidth={2}
          />
        );
      })}

      {/* ── Intrusion animation (prop-driven, one-shot) ── */}
      {intrusionPath && pathLength > 0 && (
        <motion.g
          initial={{ opacity: 1 }}
          animate={{ opacity: intrusionVisible ? 1 : 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Draw-in path */}
          <path
            d={pathD}
            fill="none"
            stroke="#ef4444"
            strokeWidth={3}
            strokeDasharray={pathLength}
            strokeDashoffset={pathLength}
            style={{
              animation: `svgDrawIn_${pathLength} 1.5s linear 0.5s forwards`,
            }}
          />

          {/* Travelling attacker circle */}
          <motion.circle
            r={8}
            fill="#ef4444"
            style={{ filter: 'drop-shadow(0 0 6px #ef4444)' }}
            initial={{ cx: attackCX[0], cy: attackCY[0] }}
            animate={{ cx: attackCX, cy: attackCY }}
            transition={{
              duration: 1.5,
              delay: 0.5,
              times,
              ease: 'linear',
            }}
          />
        </motion.g>
      )}

      {/* ── Nodes ── */}
      {NODE_DEFS.map((node) => {
        const colour = COLOR_MAP[resolvedStates[node.id]] ?? COLOR_MAP.green;
        return (
          <g key={node.id}>
            {/* Outer glow ring */}
            <circle
              cx={node.cx}
              cy={node.cy}
              r={28}
              fill="none"
              stroke={colour}
              strokeWidth={2}
              opacity={0.35}
            />
            {/* Main node circle */}
            <circle
              cx={node.cx}
              cy={node.cy}
              r={22}
              fill={colour}
              stroke="#1f2937"
              strokeWidth={2}
            />
            {/* Label above */}
            <text
              x={node.cx}
              y={node.cy - 36}
              textAnchor="middle"
              fontSize={labelFontSize}
              fontFamily="system-ui, sans-serif"
              fill="#e5e7eb"
              fontWeight="600"
            >
              {node.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default NetworkTopology;
